import {useCallback, useEffect, useMemo, useRef, Fragment, useState} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {getAspectRatioFromImage, getCssSizeValue, getFontSize, isObjEmpty} from '@repo/utils';
import {  setSelectedSubItem } from '@/state/menuSlice';
import { useRouter } from 'next/router';
import { Image } from '@repo/ui'
import InfiniteLooper from "@/components/Menu/desktop/VerticalLoop";
import { gsap, useGSAP } from '@repo/utils/gsap'
import {BREAKPOINTS} from "@repo/ui/themeSettings";
import { CONTENT_TYPES as COMMON_CONTENT_TYPES } from '@repo/utils/constants';

const StyledWrapper = styled.div`
	display: none;

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        transform: translateY(100%);
		display: block;
		position: fixed;
		height: 100vh;
		width: 30vw;
		right: 15%;
		z-index: 99;

		.container {
			width: 100%;
			height: 100%;
		}

		.menu-carousel-item {
            width: 100%;
            height: auto;
			margin: ${getCssSizeValue(20, BREAKPOINTS.DESKTOP)} 0;
			cursor: pointer;
            position: relative;
            overflow: hidden;
            
            .scale-container {
                transition: all 0.3s ease-in-out;
                &:hover {
                    transform: scale(1.05)
                }
            }
		}
	}
`;

const MenuCarouselItem = styled.div`
    width: 100%;
    height: auto;
    margin: ${getCssSizeValue(20, BREAKPOINTS.DESKTOP)} 0;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    .scale-container {
        transition: all 0.3s ease-in-out;
        &:hover {
            transform: scale(1.05)
        }
    }
    &.disabled {
        cursor: default;
    }
`

const SingleThumbnailWrapper = styled.div`
    width: 100%;
	height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
	cursor: pointer;
    margin-bottom: ${getCssSizeValue(30)};
	&.desktop {
        display: flex;
	}
    p.title {
		span {
			background-image: linear-gradient(180deg,transparent 97%, var(--secondary-color) 0);
			background-size: 0 100%;
			background-repeat: no-repeat;
			text-decoration: none;
			transition: background-size 0.4s ease 0s;
		}
		&.hover {
			span {
				transition: background-size 0.4s ease 0s;
				background-size: 100% 100%;
			}
		}
        font-size: ${getFontSize(34, BREAKPOINTS.DESKTOP)};
        line-height: ${getFontSize(54, BREAKPOINTS.DESKTOP)};
        margin-bottom: ${getCssSizeValue(33, BREAKPOINTS.DESKTOP)};
    }
`

const SingleCardContainer = styled.div`
	overflow: hidden;
	position: relative;
`

const SingleCard = styled.div`
    width: 100%;
    height: auto;
    position: relative;
    aspect-ratio: ${({aspectRatio}) => aspectRatio ? aspectRatio : 16/9};
	transition: transform 0.3s ease-in-out;
	transform: scale(1);
	&.hover {
		transition: transform 0.3s ease-in-out;
		transform: scale(1.05)
	}
`

const StyledCardWithTextsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`

const StyledPleoTag = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 106px;
    height: 33px;
    z-index: 2;
    border-radius: 0 0 0 13px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    &.secondary {
        color: var(--primary-color);
    }
    &.primary {
        color: var(--secondary-color);
    }
    p {
        font-weight: ${({theme}) => theme.fontWeight.bold};
        font-size: ${getFontSize(14, BREAKPOINTS.DESKTOP)};
        line-height: ${getFontSize(54, BREAKPOINTS.DESKTOP)};
        margin-bottom: ${getCssSizeValue(33, BREAKPOINTS.DESKTOP)};
    }
    &.pleo-digital {
        background-color: #E84F44;
    }
    &.pleo-design {
        background-color: #2AC1D7;
    }
    &.pleo-method {
        background-color: #9747FF;
    }    
  	&.pleo-group {
        background-color: #1a303c;
    }
`

const BlogThumbnailCarouselItem = ({item}) => {
    const id = item?.id || ''
    const title = item?.blogPageName || ''
    const slug = item?.slug || ''
    const pleoCategory = item?.pleoCategory || ''
    const assetUrl = item?.thumbnailImage?.url || ''
    const assetAlt = item?.thumbnailImage?.alt || ''
    const aspectRatio = getAspectRatioFromImage(item?.thumbnailImage || {})

    const { locale } = useRouter()
    const [hover, setHover] = useState(false)

    const handleOnClick = () => {
        const newWindow = window.open(`https://pleo-ultimate-monorepo-group.vercel.app${locale=== 'pl' ? '/pl/' :'/'}news/${slug}`, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
    };

    const handleHover = () => setHover(prevValue => !prevValue)

    return (
        <>
            <SingleThumbnailWrapper
                key={id}
                onClick={handleOnClick}
                onPointerEnter={handleHover}
                onPointerLeave={handleHover}
            >
                <StyledCardWithTextsWrapper>
                    <SingleCardContainer>
                        <StyledPleoTag className={`${pleoCategory}`}>pleo {pleoCategory.split('-')[1]}</StyledPleoTag>
                        <SingleCard className={`${hover ? 'hover' : ''} `} aspectRatio={aspectRatio}>
                            <Image aspectRatio={aspectRatio} src={assetUrl} alt={assetAlt} sizes={"100%"} priority />
                        </SingleCard>
                    </SingleCardContainer>
                    <p className={`${hover ? 'hover title' : 'title'} `}><span>{title}</span></p>
                </StyledCardWithTextsWrapper>
            </SingleThumbnailWrapper>
        </>
    )
}

const ImageCarouselItem = ({item, index, onClick, onHover, slug, isDisabled}) => {
    const aspectRatio = getAspectRatioFromImage(item?.menuPreviewImage || {})

    return (
        <MenuCarouselItem
            key={index}
            className={isDisabled ? 'disabled' : "menu-carousel-item"}

        >
            <div
                className='scale-container'
            >
                <Image
                    fill
                    priority
                    style={{ width: '100%', height: 'auto', aspectRatio}}
                    onPointerEnter={() => onHover(item)}
                    onClick={isDisabled ? null : () => onClick(slug)}
                    key={item + index}
                    src={item?.menuPreviewImage?.url || ''}
                    alt={item?.menuPreviewImage?.alt || ''}
                />
            </div>
        </MenuCarouselItem>
    );
}

const CarouselItem = ({item, index, onClick, onHover, slug, isDisabled}) => {
    const typeName = item?.__typename || ''

    return typeName === COMMON_CONTENT_TYPES.GROUP_BLOG_PAGE_RECORD
        ? <BlogThumbnailCarouselItem
            item={item}
            key={index}
            isDisabled={isDisabled}
        />
        : <ImageCarouselItem
            onClick={onClick}
            onHover={onHover}
            slug={slug}
            item={item}
            index={index}
            isDisabled={isDisabled}
        />;
}

const VerticalCarousel = () => {
    const dispatch = useDispatch();
    const { push, asPath } = useRouter();
    const { selectedItem, open: isMenuOpen, isCarouselVisible } = useSelector((state) => state.menu);
    const containerRef = useRef();
    const slug = useMemo(() => {
        if (isObjEmpty(selectedItem)) return false;
        return `/${selectedItem?.link?.slug}` || '';
    }, [selectedItem]);

    const disableCarouselOnHover = selectedItem?.link?.menuContext?.disableCarouselOnHover
    const disableMenuItems = selectedItem?.disableMenuItem || false;
    useEffect(() => {
        !isObjEmpty(selectedItem) && dispatch(setSelectedSubItem(selectedItem?.link?.menuContext?.previewItems[0]))
    }, [selectedItem])

    useGSAP(
        () => {
            if (isObjEmpty(selectedItem)) return;
            gsap.to('.container', { y: '0' });
        },
        { scope: containerRef, dependencies: [selectedItem, isCarouselVisible] }
    );

    const onClick = useCallback(
        (slug) => {
            push(slug, slug);
        },
        [dispatch, push]
    );

    const onHover = useCallback(
        (item) => dispatch(setSelectedSubItem(item)),
        [dispatch]
    );

    const selectedItemPreviewsItemsLength = useMemo(() => {
        if (isObjEmpty(selectedItem)) return 0;

        return selectedItem?.link?.menuContext?.previewItems?.length || 0
    }, [selectedItem])

    return (
        <StyledWrapper ref={containerRef} className="vertical-carousel-wrapper container">
            {isCarouselVisible && (
                <InfiniteLooper numberOfItems={selectedItemPreviewsItemsLength} speed={1.25} direction="up" disabled={disableCarouselOnHover}>
                    {!isObjEmpty(selectedItem) &&
                        selectedItem?.link?.menuContext?.previewItems?.length === 1 && (
                             <CarouselItem
                                item={selectedItem?.link?.menuContext?.previewItems[0] || {}}
                                onClick={onClick}
                                onHover={onHover}
                                slug={slug}
                                isDisabled={disableMenuItems}
                            />
                        )}
                    {!isObjEmpty(selectedItem) &&
                        selectedItem?.link?.menuContext?.previewItems?.length > 1 &&
                        [
                            ...selectedItem?.link?.menuContext?.previewItems || []
                        ].map((item, index) => {
                            const itemSlug = `${slug}/${item?.slug || ''}`
                            return <CarouselItem
                                key={item?.id || index}
                                item={item}
                                index={index}
                                onClick={onClick}
                                onHover={onHover}
                                slug={itemSlug}
                                isDisabled={disableMenuItems}
                            />
                        }
                    )}
                </InfiniteLooper>
            )}
        </StyledWrapper>
    );
};

export default VerticalCarousel;
